/**
 * permissionModules.js — SINGLE SOURCE OF TRUTH for permission modules.
 *
 * HOW TO ADD A NEW MODULE:
 *   1. Add an entry to PERMISSION_MODULES below.
 *   2. That's it. The permission matrix renders automatically.
 *      No other file needs to change.
 *
 * HOW TO ADD A NEW SUBMODULE:
 *   Add to the parent module's `submodules` array.
 *
 * KEY FORMAT:
 *   Module  → "hr"
 *   Submodule → "hr:employees"   (parent:submodule)
 *   Settings  → "hr:settings:shifts" (parent:settings:subpage)
 */

export const ACTIONS = ['view', 'add', 'edit', 'delete', 'export'];

export const PERMISSION_MODULES = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    submodules: [
      { key: 'dashboard:overview', label: 'Executive Overview' },
      { key: 'dashboard:sales', label: 'Sales Dashboard' },
      { key: 'dashboard:hr', label: 'HR Dashboard' },
      { key: 'dashboard:finance', label: 'Finance Dashboard' },
      { key: 'dashboard:it', label: 'IT Dashboard' },
    ],
  },
  {
    key: 'home',
    label: 'Home',
    submodules: [
      { key: 'home:settings:my-organization', label: 'Settings - My Organization' },
      { key: 'home:settings:divisions',       label: 'Settings - Divisions' },
      { key: 'home:settings:branches',        label: 'Settings - Branches' },
      { key: 'home:settings:departments',     label: 'Settings - Departments' },
      { key: 'home:settings:designations',    label: 'Settings - Designations' },
      { key: 'home:settings:notifications',   label: 'Settings - Notifications' },
      { key: 'home:settings:widgets',         label: 'Settings - Dashboard Widgets' },
    ],
  },
  {
    key: 'sales',
    label: 'Sales',
    submodules: [
      { key: 'sales:dashboard',     label: 'Sales Dashboard' },
      { key: 'sales:overview',     label: 'Sales Overview' },
      { key: 'sales:clients',       label: 'Clients' },
      { key: 'sales:leads',         label: 'Leads' },
      { key: 'sales:expo-leads',    label: 'Expo Leads' },
      { key: 'sales:orders',        label: 'Sales Orders' },
      { key: 'sales:opportunities', label: 'Opportunities' },
      { key: 'sales:quotations',    label: 'Quotations' },
      { key: 'sales:channels',      label: 'Sales Channels' },
      { key: 'sales:delivery',      label: 'Delivery Management' },
      { key: 'sales:invoices',      label: 'Invoices' },
      { key: 'sales:payments',      label: 'Payments' },
      { key: 'sales:returns',       label: 'Sales Returns' },
      // Sales Settings
      { key: 'sales:settings:pipeline-stages',    label: 'Settings - Sales Pipeline Stages' },
      { key: 'sales:settings:quotation-templates', label: 'Settings - Quotation Templates' },
      { key: 'sales:settings:tax-pricing',         label: 'Settings - Tax & Pricing Rules' },
      { key: 'sales:settings:expo-management',     label: 'Settings - Expo Management' },
      { key: 'sales:settings:business-verticals',     label: 'Settings - Business Verticals' },
      { key: 'sales:settings:business-categories',     label: 'Settings - Bussiness Categories' },
      { key: 'sales:settings:programs',     label: 'Settings - Programs' },
    ],
  },
  {
    key: 'marketing',
    label: 'Marketing',
    submodules: [
      { key: 'marketing:dashboard',  label: 'Marketing Dashboard' },
      { key: 'marketing:campaigns',  label: 'Campaigns' },
      { key: 'marketing:promotions', label: 'Promotions' },
      { key: 'marketing:audience',   label: 'Audience' },
      // Marketing Settings
      { key: 'marketing:settings:email-templates', label: 'Settings - Email Templates' },
      { key: 'marketing:settings:integrations',    label: 'Settings - Integrations' },
    ],
  },
  {
    key: 'finance',
    label: 'Finance',
    submodules: [
      { key: 'finance:dashboard', label: 'Finance Dashboard' },
      { key: 'finance:accounts',  label: 'Accounts' },
      { key: 'finance:payments',  label: 'Payments' },
      { key: 'finance:expenses',  label: 'Expenses' },
      { key: 'finance:taxation',  label: 'Taxation' },
      // Finance Settings
      { key: 'finance:settings:chart-of-accounts', label: 'Settings - Chart of Accounts' },
      { key: 'finance:settings:currency-tax',      label: 'Settings - Currency & Tax' },
    ],
  },
  {
    key: 'inventory',
    label: 'Inventory',
    submodules: [
      { key: 'inventory:dashboard',       label: 'Inventory Dashboard' },
      { key: 'inventory:stock',           label: 'Stock Overview' },
      { key: 'inventory:purchase-orders', label: 'Purchase Orders' },
      { key: 'inventory:suppliers',       label: 'Suppliers' },
      // Inventory Settings
      { key: 'inventory:settings:warehouses',   label: 'Settings - Warehouses' },
      { key: 'inventory:settings:stock-alerts', label: 'Settings - Stock Alerts' },
    ],
  },
  {
    key: 'logistics',
    label: 'Logistics',
    submodules: [
      { key: 'logistics:dashboard', label: 'Logistics Dashboard' },
      { key: 'logistics:shipments', label: 'Shipments' },
      { key: 'logistics:tracking',  label: 'Delivery Tracking' },
      { key: 'logistics:vendors',   label: 'Vendors' },
      // Logistics Settings
      { key: 'logistics:settings:shipping-zones', label: 'Settings - Shipping Zones' },
      { key: 'logistics:settings:carrier-setup',   label: 'Settings - Carrier Setup' },
    ],
  },
  {
    key: 'services',
    label: 'Services',
    submodules: [
      { key: 'services:dashboard', label: 'Services Dashboard' },
      { key: 'services:requests',  label: 'Service Requests' },
      { key: 'services:catalogue', label: 'Service Catalogue' },
      // Services Settings
      { key: 'services:settings:categories', label: 'Settings - Service Categories' },
      { key: 'services:settings:sla-rules',  label: 'Settings - SLA Rules' },
    ],
  },
  {
    key: 'hr',
    label: 'HR',
    submodules: [
      { key: 'hr:dashboard',         label: 'HR Dashboard' },
      { key: 'hr:employees',         label: 'Employee Management' },
      { key: 'hr:recruitment',         label: 'Recruitment Management' },
     
      //Recruitment Tabs
      { key: 'hr:recruitment-overview',  label: 'Recruitment overview Tab' },
      { key: 'hr:recruitment-jobs',  label: 'Recruitment Jobs Tab' },
      { key: 'hr:recruitment-candidates',  label: 'Recruitment Candidates Tab' },
      { key: 'hr:recruitment-onboarding',  label: 'Recruitment Onboarding Tab' },
      { key: 'hr:recruitment-offers',  label: 'Recruitment Offers Tab' },

      { key: 'hr:divisions',         label: 'Divisions' },
      { key: 'hr:departments',       label: 'Departments' },
      { key: 'hr:attendance',        label: 'Attendance Management' },
      //tabs under attendance
      { key: 'hr:regularize',                     label: 'Regularizations Approve Tab' },
      { key: 'hr:all-regularize-approval',        label: 'All Regularizations Tabs' },
      
      { key: 'hr:leave',             label: 'Leave Management' },
      //Leave Tabs
      { key: 'hr:leave-approvals',   label: 'Leave Approvals' },
      { key: 'hr:all-leaves',        label: 'All Leaves' },
      { key: 'hr:leave-types',       label: 'Leave Types' },
      { key: 'hr:leave-policies',       label: 'Leave Policies' },
      { key: 'hr:employee-balances',       label: 'Employee Balances' },
      
      { key: 'hr:payroll',       label: 'Payroll Management' },
      { key: 'hr:expenses',      label: 'Expense Management' },
      { key: 'hr:documents',     label: 'Document Management' },
      
      { key: 'hr:performance',   label: 'Performance Management' },
      //KRA tabs
      { key: 'hr:kra',           label: 'KRA' },
      { key: 'hr:kra-overview',  label: 'KRA Overview Tab' },
      { key: 'hr:kra-library',   label: 'KRA Library Tab' },
      { key: 'hr:tag-kra',       label: 'Tag KRA Tab' },
      { key: 'hr:tag-kra-list',  label: 'Tagged KRA List Tab' },
      { key: 'hr:review-list',   label: 'Review List Tab' },
      { key: 'hr:workflows',     label: 'Workflows Tab' },

      { key: 'hr:lms',           label: 'LMS Management' },
      { key: 'hr:letter-policy', label: 'HR Letter & Policy' },
      //policies & letters tabs
      { key: 'hr:policies', label: 'Policies Tab' },
      { key: 'hr:case-tracker',  label: 'HR Case Tracker' },
      { key: 'hr:team-cases-tab',  label: 'Team Cases Tab' },
      { key: 'hr:all-cases-tab',  label: 'All Cases Tab' },
      { key: 'hr:exit',          label: 'Exit Management' },
      // HR Settings
      { key: 'hr:settings:shifts',         label: 'Settings - Shifts' },
      { key: 'hr:settings:holidays',       label: 'Settings - Holidays' },
      { key: 'hr:settings:leave-types',     label: 'Settings - Leave Types' },
      { key: 'hr:settings:workflows',       label: 'Settings - Review Workflows' },
      { key: 'hr:settings:case-category',       label: 'Settings - Case Category' },
      { key: 'hr:settings:payroll-setup',   label: 'Settings - Payroll Setup' },
      { key: 'hr:settings:leave-policies',  label: 'Settings - Leave Policies' },
    ],
  },
  {
    key: 'it',
    label: 'IT',
    submodules: [
      { key: 'it:dashboard', label: 'IT Dashboard' },
      { key: 'it:assets',  label: 'Assets' },
      { key: 'it:tickets', label: 'Tickets' },
      { key: 'it:access',  label: 'Access Management' },
      // IT Settings
      { key: 'it:settings:asset-categories', label: 'Settings - Asset Categories' },
      { key: 'it:settings:ticket-priorities', label: 'Settings - Ticket Priorities' },
    ],
  },
  {
    key: 'legal',
    label: 'Legal',
    submodules: [
      { key: 'legal:dashboard', label: 'Legal Dashboard' },
      { key: 'legal:contracts',  label: 'Contracts' },
      { key: 'legal:compliance', label: 'Compliance' },
      // Legal Settings
      { key: 'legal:settings:contract-templates', label: 'Settings - Contract Templates' },
    ],
  },
  {
    key: 'security',
    label: 'Security',
    submodules: [
      { key: 'security:dashboard',   label: 'Security Dashboard' },
      { key: 'security:users',       label: 'Users' },
      { key: 'security:invitations', label: 'Invitations' },
      { key: 'security:roles',       label: 'Roles & Permissions' },
      { key: 'security:audit',       label: 'Audit Logs' },
      // Security Settings
      { key: 'security:settings:password-policy', label: 'Settings - Password Policy' },
      { key: 'security:settings:2fa',             label: 'Settings - Two-Factor Auth' },
    ],
  },
  {
    key: 'reports',
    label: 'Reports',
    submodules: [
      { key: 'reports:dashboard',    label: 'Dashboard' },
      { key: 'reports:sales',        label: 'Sales Reports' },
      { key: 'reports:financial',    label: 'Financial Reports' },
      { key: 'reports:hr',           label: 'HR Reports' },
      { key: 'reports:daily-followup', label: 'Daily Followup' },
      { key: 'reports:client-stage', label: 'Client Stage' },
      { key: 'reports:onboarding',   label: 'Onboarding Calendar' },
      { key: 'reports:mis',          label: 'MIS Report' },
      // Reports Settings
      { key: 'reports:settings:scheduled', label: 'Settings - Scheduled Reports' },
    ],
  },
  {

  key: 'automation',
  label: 'Automation',
  submodules: [
    { key: 'automation:push', label: 'Push Notifications' },
    { key: 'automation:history', label: 'Notification History' },

    // Future modules
    { key: 'automation:email', label: 'Email Automation' },
    { key: 'automation:workflow', label: 'Workflow Builder' },
    { key: 'automation:templates', label: 'Templates' },
    { key: 'automation:scheduler', label: 'Scheduler' },
  ],
},
];

// ─── Derived helpers (auto-built from above — don't edit manually) ───

/** Flat list of every key in the system (module + submodule) */
export const ALL_PERMISSION_KEYS = PERMISSION_MODULES.flatMap((m) => [
  m.key,
  ...(m.submodules || []).map((s) => s.key),
]);

/** Empty permission row */
export const emptyPerms = () => ({
  view: false, add: false, edit: false, delete: false, export: false, hide: false,
});

/** Full permission row (for owner or "select all") */
export const fullPerms = () => ({
  view: true, add: true, edit: true, delete: true, export: true, hide: false,
});

/** Build an initial permissions object with ALL keys → empty */
export const buildInitialPermissions = () => {
  const obj = {};
  ALL_PERMISSION_KEYS.forEach((key) => { obj[key] = emptyPerms(); });
  return obj;
};

/** Build full-access permissions (owner use-case) */
export const buildFullPermissions = () => {
  const obj = {};
  ALL_PERMISSION_KEYS.forEach((key) => { obj[key] = fullPerms(); });
  return obj;
};